import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { articles } from '@/mocks/articles';
import { videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-600 text-lg">文章未找到</p>
            <Link to="/" className="mt-4 inline-block px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              {t('article.backToList')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideoData = videos.filter((v) => article.relatedVideos.includes(v.id));

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('breadcrumb.article') },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    description: article.summary,
    publisher: {
      '@type': 'Organization',
      name: '91视频网站',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-user-line"></i>
                {t('article.author')}: {article.author}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-calendar-line"></i>
                {t('article.publishDate')}: {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-time-line"></i>
                {t('article.readTime')}: {article.readTime}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label">
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="mt-8 prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedVideoData.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                  {t('article.relatedVideos')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-product-shop="">
                  {relatedVideoData.map((video) => (
                    <VideoCard key={video.id} video={video} size="medium" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}